import { useEffect, useMemo, useRef, useState } from "react";
import type { MeetingTranscriptEntry } from "../../shared/types";
import { fileUrl } from "./Files";
import { Icon } from "./Icon";
import { IconButton } from "./IconButton";

type Chunk = { fileId: string; startedAt: string; endedAt: string };
type Frame = { fileId: string; at: string };

const pad = (n: number) => String(n).padStart(2, "0");
/** 75 → "1:15", 3725 → "1:02:05" */
const clock = (seconds: number) => {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`;
};

/**
 * One timeline for a meeting: the recording (played piece by piece as a single track), a scrub bar with screenshot
 * markers, what was on screen at that moment, and the transcript following along. While the meeting is live the
 * timeline grows as new pieces arrive.
 */
export function MeetingPlayer({ chunks, frames, transcript, live, startAt, fileHref }: {
  chunks: Chunk[];
  frames: Frame[];
  transcript: MeetingTranscriptEntry[];
  live: boolean;
  startAt: string;
  /** How to link a file; defaults to the signed-in files API. */
  fileHref?: (fileId: string) => string;
}) {
  const href = fileHref ?? ((id: string) => fileUrl(id, true));
  const pieces = useMemo(() => [...chunks].sort((a, b) => Date.parse(a.startedAt) - Date.parse(b.startedAt)), [chunks]);
  const shots = useMemo(() => [...frames].sort((a, b) => Date.parse(a.at) - Date.parse(b.at)), [frames]);
  const times = [startAt, pieces[0]?.startedAt, shots[0]?.at, transcript[0]?.at].map((t) => (t ? Date.parse(t) : NaN)).filter(Number.isFinite);
  const base = times.length ? Math.min(...times) : Date.now();
  const lastAt = Math.max(
    base,
    ...pieces.map((p) => Date.parse(p.endedAt)),
    ...shots.map((f) => Date.parse(f.at)),
    ...transcript.map((e) => Date.parse(e.at)),
  );
  const total = Math.max(1, (lastAt - base) / 1000);
  const hasAudio = pieces.length > 0;

  const audio = useRef<HTMLAudioElement>(null);
  const loaded = useRef(-1);
  const waitingForNext = useRef(false);
  const listRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [follow, setFollow] = useState(live);

  const offsetOf = (i: number) => (Date.parse(pieces[i]!.startedAt) - base) / 1000;
  const lengthOf = (i: number) => Math.max(0.5, (Date.parse(pieces[i]!.endedAt) - Date.parse(pieces[i]!.startedAt)) / 1000);
  /** The piece playing at `t`, or the next one after a gap; -1 past the end. */
  const pieceAt = (t: number) => {
    for (let i = 0; i < pieces.length; i++) if (t < offsetOf(i) + lengthOf(i)) return i;
    return -1;
  };

  const seek = (t: number, play: boolean) => {
    const el = audio.current;
    const target = Math.min(Math.max(0, t), total);
    if (!hasAudio || !el) {
      setPosition(target);
      if (play) setPlaying(true);
      return;
    }
    const i = pieceAt(target);
    if (i < 0) {
      el.pause();
      setPosition(target);
      setPlaying(false);
      waitingForNext.current = live && play;
      return;
    }
    const within = Math.max(0, target - offsetOf(i));
    setPosition(offsetOf(i) + within);
    const start = () => {
      el.currentTime = Math.min(within, Number.isFinite(el.duration) ? Math.max(0, el.duration - 0.05) : within);
      if (play) void el.play().then(() => setPlaying(true), () => setPlaying(false));
    };
    if (loaded.current !== i) {
      loaded.current = i;
      el.src = href(pieces[i]!.fileId);
      el.addEventListener("loadedmetadata", start, { once: true });
      el.load();
    } else {
      start();
    }
  };

  // Without a recording, "play" walks through the transcript and screenshots in real time.
  useEffect(() => {
    if (hasAudio || !playing) return;
    const timer = setInterval(() => setPosition((p) => (p + 0.25 >= total ? (setPlaying(false), total) : p + 0.25)), 250);
    return () => clearInterval(timer);
  }, [hasAudio, playing, total]);

  // Live: keep the slider at the newest moment until the viewer scrubs; resume playback when the next piece arrives.
  useEffect(() => {
    if (follow && !playing && !dragging) setPosition(total);
    if (waitingForNext.current && pieceAt(position) >= 0) {
      waitingForNext.current = false;
      seek(position, true);
    }
  }, [total, pieces.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = () => {
    const el = audio.current;
    setFollow(false);
    if (playing) {
      el?.pause();
      setPlaying(false);
      return;
    }
    seek(position >= total - 0.5 ? 0 : position, true);
  };

  const now = base + position * 1000;
  const screen = [...shots].reverse().find((f) => Date.parse(f.at) <= now + 500) ?? null;
  let activeLine = -1;
  for (let i = 0; i < transcript.length; i++) if (Date.parse(transcript[i]!.at) <= now + 500) activeLine = i;

  // Keep the current screenshot visible in the strip (scrolls the strip only, never the page).
  useEffect(() => {
    const strip = stripRef.current;
    const thumb = screen ? strip?.querySelector<HTMLElement>(`[data-shot="${screen.fileId}"]`) : null;
    if (strip && thumb) strip.scrollTo({ left: thumb.offsetLeft - strip.clientWidth / 2 + thumb.clientWidth / 2, behavior: "smooth" });
  }, [screen?.fileId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the current line in view while playing or following live.
  useEffect(() => {
    if (activeLine < 0 || (!playing && !follow)) return;
    const list = listRef.current;
    const row = list?.querySelector<HTMLElement>(`[data-line="${activeLine}"]`);
    if (list && row) list.scrollTo({ top: row.offsetTop - list.clientHeight / 2 + row.clientHeight / 2, behavior: "smooth" });
  }, [activeLine, playing, follow]);

  return (
    <div className="card overflow-hidden">
      {hasAudio && (
        <audio
          ref={audio}
          preload="none"
          onTimeUpdate={(e) => !dragging && loaded.current >= 0 && setPosition(offsetOf(loaded.current) + e.currentTarget.currentTime)}
          onEnded={() => {
            const next = loaded.current + 1;
            if (next < pieces.length) seek(offsetOf(next), true);
            else {
              setPlaying(false);
              waitingForNext.current = live;
            }
          }}
          onPause={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
        />
      )}

      {screen && (
        <a href={href(screen.fileId)} target="_blank" rel="noopener" className="block bg-black">
          <img src={href(screen.fileId)} alt="Shared screen" className="mx-auto aspect-video max-h-80 w-full object-contain" />
        </a>
      )}

      <div className="space-y-2 p-3 sm:p-4">
        <div className="flex items-center gap-3">
          <IconButton size="md" fill="black" label={playing ? "Pause" : "Play"} onClick={toggle}>
            <Icon name={playing ? "pause" : "play"} className="size-4" />
          </IconButton>
          <div className="min-w-0 flex-1">
            <div className="relative h-6">
              {/* Screenshot markers */}
              {shots.map((f) => {
                const left = ((Date.parse(f.at) - base) / 1000 / total) * 100;
                return (
                  <button
                    key={f.fileId}
                    type="button"
                    title={`Screen at ${clock((Date.parse(f.at) - base) / 1000)}`}
                    onClick={() => seek((Date.parse(f.at) - base) / 1000, playing)}
                    className="absolute top-0 z-10 h-2 w-1 -translate-x-1/2 rounded-full bg-haze-deep hover:bg-ink"
                    style={{ left: `${Math.min(100, Math.max(0, left))}%` }}
                  />
                );
              })}
              <input
                type="range"
                min={0}
                max={total}
                step={0.1}
                value={Math.min(position, total)}
                aria-label="Meeting timeline"
                onPointerDown={() => {
                  setDragging(true);
                  setFollow(false);
                }}
                onPointerUp={(e) => {
                  setDragging(false);
                  seek(Number(e.currentTarget.value), playing);
                }}
                onChange={(e) => {
                  setPosition(Number(e.target.value));
                  if (!dragging) seek(Number(e.target.value), playing);
                }}
                className="absolute inset-x-0 bottom-0 h-4 w-full cursor-pointer accent-ink dark:accent-white"
              />
            </div>
            <div className="mt-1 flex items-center justify-between text-tiny text-muted tabular-nums">
              <span>{clock(position)}</span>
              {live && (
                <button
                  type="button"
                  onClick={() => {
                    setFollow(true);
                    seek(hasAudio ? offsetOf(pieces.length - 1) : total, playing);
                  }}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${follow ? "bg-danger/10 text-danger" : "bg-fill text-muted"}`}
                >
                  <span className={`size-1.5 rounded-full ${follow ? "animate-pulse bg-danger" : "bg-faint"}`} /> Live
                </button>
              )}
              <span>{clock(total)}</span>
            </div>
          </div>
        </div>
        {!hasAudio && (
          <p className="text-xs text-muted">No recording for this meeting (audio transcription was off). The timeline moves through the transcript and screenshots.</p>
        )}

        {shots.length > 0 && (
          <div ref={stripRef} className="scroll-thin relative -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            {shots.map((f) => {
              const t = (Date.parse(f.at) - base) / 1000;
              const current = screen?.fileId === f.fileId;
              return (
                <button
                  key={f.fileId}
                  data-shot={f.fileId}
                  type="button"
                  onClick={() => seek(t, playing)}
                  className={`shrink-0 overflow-hidden rounded-lg ring-2 transition ${current ? "ring-ink dark:ring-white" : "ring-transparent opacity-70 hover:opacity-100"}`}
                >
                  <img src={href(f.fileId)} alt="" loading="lazy" className="aspect-video w-28 bg-black object-cover" />
                  <span className="block bg-black/80 py-0.5 text-center text-tiny text-white tabular-nums">{clock(t)}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div ref={listRef} className="scroll-thin relative max-h-96 overflow-auto border-t border-line p-2">
        {transcript.map((e, i) => {
          const t = (Date.parse(e.at) - base) / 1000;
          const current = i === activeLine;
          return (
            <button
              key={i}
              data-line={i}
              type="button"
              onClick={() => {
                setFollow(false);
                seek(t, true);
              }}
              className={`flex w-full gap-3 rounded-lg px-2 py-1.5 text-left text-sm transition-colors ${
                current ? "bg-haze-soft dark:bg-haze/15" : "hover:bg-fill-subtle"
              } ${i > activeLine && (playing || position > 0) ? "text-muted" : ""}`}
            >
              <span className="w-12 shrink-0 pt-px font-mono text-tiny text-faint tabular-nums">{clock(t)}</span>
              <span className="min-w-0 flex-1 break-words">
                <b className="font-medium">{e.speaker}</b> <span>{e.text}</span>
              </span>
            </button>
          );
        })}
        {transcript.length === 0 && <p className="p-3 text-xs text-faint">Nothing captured yet.</p>}
      </div>
    </div>
  );
}
